// src/routes/RDM/EcrPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

import {
  // Languages,
  Target,
  Users,
  Sparkles,
  BookOpen,
  CheckCircle2,
  CalendarDays,
  ExternalLink,
  Star,
} from "lucide-react";
import { EcrLang, ECR_CONTENT } from "../../i18n/ecrFullContent";
// import HeaderImage from "../../components/HeaderImage";
import { Link, useNavigate } from "react-router-dom";
import { BASE_API_URL } from "../../utils/constants";
import { useQuery } from "react-query";
import HeaderBanner from "../../components/Header";
import ShareBar from "../../components/ShareBar";

// Simple icon cycle for objective cards
const ObjIcons = [Target, Users, BookOpen, Sparkles, CheckCircle2];

type PublicEcrPost = {
  _id: string;
  title: string;
  slug: string;
  author: string;
  date: string;
  mainImage?: { url: string; alt?: string };
};

const STAR_API_URL = `${BASE_API_URL}libsense/ecr-posts/starbymonth`;

const EcrPage: React.FC = () => {
  const [lang, setLang] = useState<EcrLang>("en");
    const navigate = useNavigate();

  // Optional: honor ?lang=en|fr|pt
  useEffect(() => {
    const q = (
      new URL(window.location.href).searchParams.get("lang") || ""
    ).toLowerCase();
    if (q === "fr" || q === "pt" || q === "en") setLang(q as EcrLang);
  }, []);

  const copy = useMemo(() => ECR_CONTENT[lang], [lang]);

  // tiny animation helpers
  const fade = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };

    // Star blog
    const { data: starData } = useQuery(
      ["starBlog"],
      async (): Promise<{ post: PublicEcrPost }> => {
        const res = await fetch(STAR_API_URL, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      },
      { refetchOnWindowFocus: false }
    );
  
  
      useEffect(() => {
        const element = document.getElementById("ecr");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, []);

  return (
    <>
        <HeaderBanner />

      {/* Lock this page to the original language copies (no Google auto-translate) */}
      <div  id="ecr" >
        {/* HERO */}
        <section className="relative overflow-hidden">
          {/* <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none" /> */}
          <div className="max-w-5xl mx-auto mt-4 px-4 sm:px-6 lg:px-8">

            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-center text-xxl font-semibold text-foreground uppercase mb-4">
                LIBSENSE Early Career Researchers (ECR) Initiative
              </h2>
              <p className="mt-3 text-sm text-foreground/90">
                The LIBSENSE Early Career Researchers (ECRs) initiative aims to
                support the development and empowerment of ECRs in Africa. The
                ECRs include postgraduate students, postdoctoral researchers,
                new faculty members and researchers, librarians, research
                administrators and other academics; they collaborate to advance
                institutional and national Research Data Management (RDM)
                policies and practices.
              </p>
              <p className="mt-3 text-sm text-foreground/90">
                Focusing on best practices in RDM and research ethics, the
                initiative promotes the establishment of ECR Advocacy Groups in
                various African countries. The LIBSENSE ECR initiative supports
                open science and responsible research practices to enhance
                research visibility, transparency, and quality throughout the
                African continent.
              </p>
            </motion.div>
          </div>
        </section>

        {/* BACKGROUND */}
        <section className="max-w-5xl mx-auto mt-12 px-4 sm:px-6 lg:px-8">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
          Background
          </h2>
              <p className="mt-3 text-sm text-foreground/90">
              The ECR initiative emerged as part of LIBSENSE's mission to embed community-led, bottom-up approaches into the development of research and education systems on the continent. The initiative is an outcome of the <Link to="https://indico.wacren.net/event/186/" className="text-primary" target="_blank">National Collaboration and Capacity Building on RDM Practices and Governance Workshops</Link>. Beginning in <Link to="/ecr-nigeria" target="_blank" className="text-primary">Nigeria</Link> and <Link to="/ecr-ghana" target="_blank" className="text-primary">Ghana</Link>, the initiative has already mobilised a national team of librarians and researchers to support data management, open access, research ethics, and scholarly communication reforms in their respective academic environments.
              </p>
              <p className="mt-3 text-sm text-foreground/90">
              LIBSENSE is expanding the ECR community across institutions in Africa, fostering inclusivity, equity, regional collaboration and capacity building for open science. Express your interest in joining the LIBSENSE ECR network <Link to="/ecr-call-expansion" target="_blank" className="text-primary">here</Link>.
              </p>
        </section>

                {/* OBJECTIVES */}
                <section className="max-w-5xl mx-auto mt-10 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-primary" />
                    <h3 className="text-lg sm:text-xl font-semibold">
                      {copy.objectivesTitle}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground/80 mb-3">
                    {copy.objectivesLead}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {copy.objectives.map((text, i) => {
                      const Icon = ObjIcons[i % ObjIcons.length];
                      return (
                        <motion.div
                          key={i}
                          variants={fade}
                          initial="hidden"
                          whileInView="show"
                          viewport={{ once: true, amount: 0.2 }}
                          className="rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                              <Icon className="w-4 h-4 text-primary" />
                            </div>
                            <p className="text-sm text-foreground">{text}</p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </section>


        {/* BENEFITS */}
        <section className="max-w-5xl mx-auto mt-10 px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg sm:text-xl font-semibold mb-2">
            {copy.benefitsTitle}
          </h3>
          <p className="text-sm text-foreground/80 mb-2">{copy.benefitsLead}</p>
          <ul className="space-y-2">
            {copy.benefits.map((li, i) => (
              <li
                key={i}
                className="text-sm text-foreground flex items-start gap-2"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        </section>

                {/* BLOG OF THE MONTH */}
        <section className="max-w-5xl mx-auto mt-12 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg sm:text-xl font-semibold">
           
                 Blog of the Month
            </h3>
          </div>

          {starData?.post ? (
            <motion.div
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-md transition"
            >
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="md:col-span-1 h-56 md:h-full">
                  <img
                    src={
                      starData.post.mainImage?.url ||
                      "/assets/images/libsense.png"
                    }
                    alt={starData.post.mainImage?.alt || starData.post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-2 p-5 flex flex-col justify-between">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-foreground line-clamp-2">
                      {starData.post.title}
                    </h4>
                    <div className="mt-2 text-xs text-foreground/80 flex items-center gap-4">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="w-4 h-4 text-primary" />
                        {new Date(starData.post.date).toLocaleDateString()}
                      </span>
                      <span>•</span>
                      <span>{starData.post.author}</span>
                    </div>
                  </div>
                  <ShareBar
                    absoluteUrl={`${window.location.origin}/ecr-blog/${starData?.post.slug}`}
                    title={starData?.post.title}
                    message= "Discover more about my featured blog on the LIBSENSE website."
                    
                  />
                  <div className="mt-4">
                    <button
                      onClick={() =>
                        navigate(
                          `/ecr-blog/${encodeURIComponent(starData?.post.slug)}`
                        )
                      }
                      className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition"
                    >
                       Read blog
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No featured blog selected this month.
            </p>
          )}
        </section>

        {/* ALL BLOGS LINK */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 my-12">
          <div className="flex items-center justify-center">
            <Link
              to="/ecr-blog"
              className="inline-flex items-center gap-2 rounded-full border border-foreground px-5 py-2 text-sm text-foreground hover:text-primary transition"
            >
              Explore all blogs
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </section>
        {/* ECR in other countries */}
        <section className="max-w-5xl mx-auto my-12 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="text-lg sm:text-xl font-semibold">
            Explore ECR activities in other countries
            </h3>
          </div>

          <p className="text-sm text-foreground/80 mb-4">
           Learn more about what Early Career Researchers are doing in different African countries.
          </p>

          <div className="flex justify-center">
            <Link
              to="/ecr-countries"
              className="inline-flex items-center gap-2 rounded-full border border-foreground px-5 py-2 text-sm text-foreground hover:text-primary transition"
            >
             
                  Explore Countries
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </section>


      </div>
    </>
  );
};

export default EcrPage;
